import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';

const MEMBER_ACTION_TIMEOUT_MS = 20_000;

function allowedMemberEmail(value) {
  const email = String(value || '').trim().toLowerCase();
  return /^[^\s@]+@metrofire\.ca\.gov$/i.test(email);
}

function formatWhen(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}


function statusFor(member) {
  if (member.disabled) return { label: 'Disabled', className: 'disabled' };
  if (member.approved && !member.activated_at) return { label: 'Not Activated', className: 'pending' };
  if (member.approved) return { label: member.is_admin ? 'Admin' : 'Active', className: 'approved' };
  return { label: 'Pending', className: 'pending' };
}

async function invokeMemberProvision(body) {
  let timer = null;
  try {
    const result = await Promise.race([
      supabase.functions.invoke('admin-provision-member', { body }).then(value => ({ ...value, timedOut: false })),
      new Promise(resolve => {
        timer = window.setTimeout(() => resolve({ data: null, error: null, timedOut: true }), MEMBER_ACTION_TIMEOUT_MS);
      }),
    ]);
    return result;
  } catch (error) {
    return { data: null, error, timedOut: false };
  } finally {
    if (timer) window.clearTimeout(timer);
  }
}

const EMPTY_MEMBER = {
  fullName: '',
  email: '',
  isAdmin: false,
  restockApprover: false,
  inventoryNotify: false,
};

export default function MemberAdmin() {
  const [session, setSession] = useState(null);
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [workingId, setWorkingId] = useState('');
  const [newMember, setNewMember] = useState(EMPTY_MEMBER);
  const [provisioning, setProvisioning] = useState(false);
  const [pendingAccess, setPendingAccess] = useState(null);

  const load = async ({ announce = false } = {}) => {
    setError('');
    const keepCurrentRows = status === 'ready' && members.length > 0;
    if (keepCurrentRows) setRefreshing(true);
    else setStatus('loading');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const activeSession = sessionData?.session || null;
      setSession(activeSession);
      if (!activeSession) {
        setStatus('signed-out');
        return [];
      }

      const { data, error: rpcError } = await supabase.rpc('admin_list_members');
      if (rpcError) {
        setError(rpcError.message || 'Unable to load member accounts.');
        if (!keepCurrentRows) setStatus('error');
        return [];
      }

      const rows = Array.isArray(data) ? data : [];
      setMembers(rows);
      setStatus('ready');
      if (announce) {
        const when = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
        setMessage(`Member list refreshed at ${when}.`);
      }
      return rows;
    } catch (loadError) {
      setError(loadError?.message || 'Unable to refresh member accounts.');
      if (!keepCurrentRows) setStatus('error');
      return [];
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    pending: members.filter(item => !item.approved && !item.disabled).length,
    active: members.filter(item => item.approved && !item.disabled && item.activated_at).length,
    notActivated: members.filter(item => item.approved && !item.disabled && !item.activated_at).length,
    admins: members.filter(item => item.is_admin && item.approved && !item.disabled).length,
    restock: members.filter(item => item.restock_approver && item.approved && !item.disabled).length,
    inventory: members.filter(item => item.inventory_notify && item.approved && !item.disabled).length,
    disabled: members.filter(item => item.disabled).length,
  }), [members]);

  const provisionMember = async event => {
    event.preventDefault();
    setError('');
    setMessage('');

    const fullName = newMember.fullName.trim();
    const email = newMember.email.trim().toLowerCase();
    if (!fullName) return setError('Enter the member’s full name.');
    if (!allowedMemberEmail(email)) return setError('Use the member’s configured organization email.');

    setProvisioning(true);
    const { data, error: functionError, timedOut } = await invokeMemberProvision({
      full_name: fullName,
      email,
      is_admin: newMember.isAdmin,
      restock_approver: newMember.restockApprover,
      inventory_notify: newMember.inventoryNotify,
    });
    setProvisioning(false);

    if (timedOut) {
      await load();
      setMessage(`The server is taking longer than expected to finish ${fullName}’s welcome. I refreshed the roster. Do not click Add Member again yet — if ${email} appears as Not Activated, the account was created. Check for the welcome email before retrying.`);
      return;
    }

    if (functionError || data?.error) {
      setError(data?.error || functionError?.message || 'Unable to preload member.');
      return;
    }

    setNewMember(EMPTY_MEMBER);
    if (data?.activation_email_sent) {
      setMessage(`${fullName} is preapproved and the Line Medic welcome email was sent to ${email}.`);
    } else if (data?.member?.activated_at) {
      setMessage(`${fullName} is preapproved. Their account is already active.`);
    } else if (data?.activation_email_error) {
      setMessage(`${fullName} is preapproved, but the automatic welcome email could not be sent. Have them use Activate Account with ${email} to request a standard activation link.`);
    } else {
      setMessage(`${fullName} is preapproved. If they still need to activate, they can use Activate Account with ${email}.`);
    }
    await load();
  };

  const resendActivation = async member => {
    if (!member?.approved || member.disabled || member.activated_at) return;

    const fullName = String(member.full_name || '').trim();
    const email = String(member.email || '').trim().toLowerCase();
    if (!fullName || !email) {
      setError('This member is missing a name or organization email.');
      return;
    }

    setWorkingId(`${member.id}:activation`);
    setError('');
    setMessage('');

    const { data, error: functionError, timedOut } = await invokeMemberProvision({
      mode: 'resend_activation',
      email,
    });

    setWorkingId('');

    if (timedOut) {
      await load();
      setMessage(`The resend for ${fullName} is taking longer than expected. I refreshed the roster. Do not press Resend Activation again yet — check the member’s email or Resend first so we do not create duplicate messages.`);
      return;
    }

    if (functionError || data?.error) {
      setError(data?.error || functionError?.message || 'Unable to resend activation email.');
      return;
    }

    if (data?.member?.activated_at) setMessage(`${fullName} is already activated.`);
    else if (data?.activation_email_sent) setMessage(`Fresh activation email sent to ${email}.`);
    else if (data?.activation_email_error) setError(`Could not resend activation email to ${email}: ${data.activation_email_error}`);
    else setError(`Activation email was not sent to ${email}.`);

    await load();
  };

  const sendToolsEmail = async member => {
    if (!member?.approved || member.disabled || !member.activated_at) return;

    const email = String(member.email || '').trim().toLowerCase();
    const accessToken = String(session?.access_token || '').trim();
    if (!email || !accessToken) {
      setError('Member email or admin session is unavailable.');
      return;
    }

    setWorkingId(`${member.id}:tools`);
    setError('');
    setMessage('');

    try {
      const response = await fetch('/api/member-tools-email', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.sent !== true) {
        throw new Error(data?.error || 'Unable to send Line Medic tools email.');
      }
      setMessage(`Line Medic app and Drug Calculator email sent to ${email}.`);
    } catch (sendError) {
      setError(sendError?.message || 'Unable to send Line Medic tools email.');
    } finally {
      setWorkingId('');
    }
  };

  const requestAccessChange = (member, newAccess) => {
    setError('');
    setMessage('');
    setPendingAccess({ member, newAccess });
  };

  const confirmAccessChange = async () => {
    if (!pendingAccess?.member) return;
    const { member, newAccess } = pendingAccess;
    const verb = newAccess === 'approved' ? (member.disabled ? 're-enable' : 'approve') : 'disable';
    setPendingAccess(null);
    setWorkingId(member.id);
    setError('');
    setMessage('');

    const { data, error: rpcError } = await supabase.rpc('admin_set_member_access', {
      target_user_id: member.id,
      new_access: newAccess,
    });
    setWorkingId('');
    if (rpcError) {
      setError(rpcError.message || `Unable to ${verb} member.`);
      return;
    }

    // Update the row immediately from the authoritative RPC response, then refresh the
    // complete roster. This gives iPhone and WKWebView admins visible confirmation even
    // when a network refresh takes a moment.
    if (data?.id) {
      setMembers(current => current.map(item => item.id === data.id ? { ...item, ...data } : item));
    } else {
      setMembers(current => current.map(item => item.id === member.id
        ? { ...item, approved: newAccess === 'approved', disabled: newAccess === 'disabled' }
        : item));
    }
    setMessage(`${member.full_name || member.email} ${verb === 'disable' ? 'disabled' : verb === 're-enable' ? 're-enabled' : 'approved'} successfully.`);
    await load();
  };

  const setNotificationRole = async (member, roleName, enabled) => {
    setWorkingId(`${member.id}:${roleName}`);
    setError('');
    const { error: rpcError } = await supabase.rpc('admin_set_member_notification_role', {
      target_user_id: member.id,
      role_name: roleName,
      enabled,
    });
    setWorkingId('');
    if (rpcError) {
      setError(rpcError.message || 'Unable to update notification role.');
      return;
    }
    await load();
  };

  const setAdminRole = async (member, enabled) => {
    const action = enabled ? 'Grant' : 'Remove';
    const confirmed = window.confirm(`${action} program-admin access ${enabled ? 'to' : 'from'} ${member.full_name || member.email}?`);
    if (!confirmed) return;

    setWorkingId(`${member.id}:is_admin`);
    setError('');
    const { error: rpcError } = await supabase.rpc('admin_set_member_admin_role', {
      target_user_id: member.id,
      enabled,
    });
    setWorkingId('');
    if (rpcError) {
      setError(rpcError.message || 'Unable to update admin access.');
      return;
    }
    await load();
  };

  if (status === 'signed-out') {
    return (
      <section className="inventory-card">
        <h2>Member Management</h2>
        <p className="inventory-muted">Member-account changes require your Line Medic member session.</p>
        <button type="button" className="inventory-primary-action" onClick={() => window.location.assign('/')}>Open member sign in</button>
      </section>
    );
  }

  return (
    <section className="inventory-card inventory-member-admin">
      <div className="inventory-section-head">
        <div>
          <h2>Member Management</h2>
          <p>Preload the Line Medic roster, send branded member onboarding, track account activation, and manage program permissions.</p>
        </div>
        <button type="button" onClick={() => load({ announce: true })} disabled={refreshing || status === 'loading'}>
          {refreshing || status === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="inventory-error">{error}</div>}
      {message && <div className="inventory-member-success">{message}</div>}

      {pendingAccess && (
        <div className="inventory-card" style={{ marginBottom: 16, border: '2px solid currentColor' }} role="dialog" aria-modal="true" aria-label="Confirm member access change">
          <strong>{pendingAccess.newAccess === 'disabled' ? 'Disable member access?' : pendingAccess.member.disabled ? 'Re-enable member access?' : 'Approve member access?'}</strong>
          <p className="inventory-muted" style={{ marginBottom: 12 }}>
            {pendingAccess.member.full_name || pendingAccess.member.email}
            {pendingAccess.newAccess === 'disabled'
              ? ' will immediately lose normal Line Medic access at the next authorization check. Historical deployment, gear, inventory, and audit records are preserved.'
              : ' will be allowed to use the Line Medic features permitted by their assigned roles.'}
          </p>
          <div className="inventory-row-actions">
            <button type="button" onClick={() => setPendingAccess(null)}>Cancel</button>
            <button type="button" className={pendingAccess.newAccess === 'disabled' ? 'inventory-danger-action' : 'inventory-save'} onClick={confirmAccessChange}>
              {pendingAccess.newAccess === 'disabled' ? 'Yes, Disable Access' : pendingAccess.member.disabled ? 'Yes, Re-enable Access' : 'Yes, Approve Access'}
            </button>
          </div>
        </div>
      )}

      <form className="inventory-member-provision" onSubmit={provisionMember}>
        <div className="inventory-member-provision-head">
          <div>
            <strong>Preload Member</strong>
            <small>Add a roster member and automatically send their Line Medic account-setup email.</small>
          </div>
          <span>Preapproved</span>
        </div>
        <div className="inventory-member-provision-grid">
          <label>Full name
            <input value={newMember.fullName} onChange={event => setNewMember(value => ({ ...value, fullName: event.target.value }))} placeholder="First Last" autoComplete="off" required />
          </label>
          <label>Organization email
            <input type="email" value={newMember.email} onChange={event => setNewMember(value => ({ ...value, email: event.target.value }))} placeholder="name@agency.gov" autoComplete="off" required />
          </label>
        </div>
        <div className="inventory-member-provision-options">
          <label><input type="checkbox" checked={newMember.isAdmin} onChange={event => setNewMember(value => ({ ...value, isAdmin: event.target.checked }))} /> Program admin</label>
          <label><input type="checkbox" checked={newMember.restockApprover} onChange={event => setNewMember(value => ({ ...value, restockApprover: event.target.checked }))} /> Restock emails</label>
          <label><input type="checkbox" checked={newMember.inventoryNotify} onChange={event => setNewMember(value => ({ ...value, inventoryNotify: event.target.checked }))} /> Inventory emails</label>
        </div>
        <div className="inventory-member-provision-actions">
          <button type="submit" className="inventory-save" disabled={provisioning}>{provisioning ? 'Adding & Sending…' : 'Add Member & Send Welcome'}</button>
          <small>No temporary password is created. They’ll receive the branded welcome email, choose their own password, and can use <strong>Activate Account</strong> later if they need another link.</small>
        </div>
      </form>

      <div className="inventory-member-summary">
        <div><small>Active</small><strong>{counts.active}</strong></div>
        <div><small>Not Activated</small><strong>{counts.notActivated}</strong></div>
        <div><small>Pending</small><strong>{counts.pending}</strong></div>
        <div><small>Admins</small><strong>{counts.admins}</strong></div>
        <div><small>Restock Team</small><strong>{counts.restock}</strong></div>
        <div><small>Inventory Team</small><strong>{counts.inventory}</strong></div>
        <div><small>Disabled</small><strong>{counts.disabled}</strong></div>
      </div>

      {status === 'loading' && <div className="inventory-empty">Loading member accounts…</div>}
      {status === 'ready' && members.length === 0 && <div className="inventory-empty">No member accounts yet.</div>}

      {status === 'ready' && members.map(member => {
        const memberStatus = statusFor(member);
        const isSelf = member.id === session?.user?.id;
        const activationWorking = workingId === `${member.id}:activation`;
        const toolsWorking = workingId === `${member.id}:tools`;
        const adminWorking = workingId === `${member.id}:is_admin`;
        const restockWorking = workingId === `${member.id}:restock_approver`;
        const inventoryWorking = workingId === `${member.id}:inventory_notify`;
        const canManageRoles = member.approved && !member.disabled;
        const canSendTools = member.approved && !member.disabled && Boolean(member.activated_at);

        return (
          <div className="inventory-member-row" key={member.id}>
            <div className="inventory-member-main">
              <div className="inventory-member-name-line">
                <strong>{member.full_name || 'Unnamed member'}</strong>
                <span className={`inventory-member-pill ${memberStatus.className}`}>{memberStatus.label}</span>
                {member.restock_approver && canManageRoles && <span className="inventory-member-pill self">Restock Team</span>}
                {member.inventory_notify && canManageRoles && <span className="inventory-member-pill self">Inventory Team</span>}
                {isSelf && <span className="inventory-member-pill self">You</span>}
              </div>
              <span>{member.email}</span>
              <small>{member.activated_at ? `Activated: ${formatWhen(member.activated_at)}` : 'Account has not been activated yet'}{' · '}Last seen: {formatWhen(member.last_seen_at)}</small>
            </div>

            <div className="inventory-row-actions">
              {!member.approved && !member.disabled && <button type="button" className="inventory-save" disabled={workingId === member.id} onClick={() => requestAccessChange(member, 'approved')}>Approve</button>}
              {member.disabled && <button type="button" className="inventory-save" disabled={workingId === member.id} onClick={() => requestAccessChange(member, 'approved')}>Re-enable</button>}

              {member.approved && !member.disabled && !member.activated_at && (
                <button type="button" className="inventory-save" disabled={activationWorking} onClick={() => resendActivation(member)}>
                  {activationWorking ? 'Sending…' : 'Resend Activation'}
                </button>
              )}

              {canSendTools && (
                <button type="button" className="inventory-save" disabled={toolsWorking} onClick={() => sendToolsEmail(member)}>
                  {toolsWorking ? 'Sending…' : 'Send App + Drug Calc'}
                </button>
              )}

              {canManageRoles && (
                <>
                  <button type="button" className={member.is_admin ? 'inventory-save' : ''} disabled={adminWorking || (isSelf && member.is_admin)} title={isSelf && member.is_admin ? 'You cannot remove your own admin access.' : undefined} onClick={() => setAdminRole(member, !member.is_admin)}>
                    {adminWorking ? 'Saving…' : member.is_admin ? 'Admin access: On' : 'Admin access: Off'}
                  </button>
                  <button type="button" className={member.restock_approver ? 'inventory-save' : ''} disabled={restockWorking} onClick={() => setNotificationRole(member, 'restock_approver', !member.restock_approver)}>
                    {restockWorking ? 'Saving…' : member.restock_approver ? 'Restock emails: On' : 'Restock emails: Off'}
                  </button>
                  <button type="button" className={member.inventory_notify ? 'inventory-save' : ''} disabled={inventoryWorking} onClick={() => setNotificationRole(member, 'inventory_notify', !member.inventory_notify)}>
                    {inventoryWorking ? 'Saving…' : member.inventory_notify ? 'Inventory emails: On' : 'Inventory emails: Off'}
                  </button>
                </>
              )}

              {member.approved && !member.disabled && !isSelf && <button type="button" className="inventory-danger-action" disabled={workingId === member.id} onClick={() => requestAccessChange(member, 'disabled')}>Disable</button>}
            </div>
          </div>
        );
      })}
    </section>
  );
}
